import { Box, Divider } from '@mui/material';
import { Group, Panel, Separator } from 'react-resizable-panels';

import searchEngine from './Core/SearchEngine';
import useSearchResults from './useSearchResults';
import SearchQueryInput from './UI/SearchQueryInput';
import SearchResultList from './UI/SearchResultList';
import SearchMetaBar from './UI/SearchMetaBar';
import { loadSearchLayout, saveSearchLayout } from './searchLayoutHelper';

import ContentPanel from '../../../components/shared/ContentPanel';
import { InspectorProvider } from '../../../contexts/InspectorContext';

import { TabNames } from '../../../services/wss/events/TabNames';
import AppContext from '../../../controllers/AppContext';
import useKeyboardNav from '../../../hooks/useKeyboardNav';

import './YourSearchTab.css';

/**
 * YourSearchTab — entry point for the Search feature.
 * No Context/Provider — each child (SearchQueryInput, SearchMetaBar, SearchResultList)
 * calls useSearchQuery/useSearchResults directly, same as CalendarCard calls useCalEventEdit.
 * This component only needs the subset required for useKeyboardNav + ContentPanel.
 */
export default function YourSearchTab() {
    AppContext.setCurrentTab(TabNames.Search);

    const { results, total, page, pageSize, selectedEntry } = useSearchResults();
    const totalPages = Math.ceil(total / pageSize) || 1;

    useKeyboardNav({
        entries: results,
        selectedRk: selectedEntry?.rk,
        onSelect: (entry) => searchEngine.selectEntry(entry),
        meta: { total, page },
        totalPages,
        onPageChange: (p, direction) => searchEngine.loadPage(p, direction),
    });

    /**
     * Enter in search input — select first result and focus the result list.
     * DOM-lookup by id, same style as the rest of this feature (e.g. .ys-search-input
     * lookups in SearchResultList) — no ref/forwardRef needed for a one-off focus call.
     */
    const handleSearchEnter = () => {
        if (results.length === 0) return;
        searchEngine.selectEntry(results[0]);
        document.getElementById('ys-result-list')?.focus();
    };

    return (
        <InspectorProvider>
            <Box className="your-search-root">
                <Group
                    id="search-panel-group"
                    orientation="horizontal"
                    defaultLayout={loadSearchLayout()}
                    onLayoutChange={saveSearchLayout}
                    style={{ flex: 1, minHeight: 0, width: '100%' }}
                >
                    <Panel id="search-list-panel" defaultSize={30} minSize={20}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <SearchQueryInput onEnter={handleSearchEnter} />
                            <SearchMetaBar />
                            <Divider sx={{ borderBottom: '0.5px solid #294766' }} />
                            <SearchResultList />
                        </Box>
                    </Panel>

                    <Separator className="j-splitter" />

                    <Panel id="search-content-panel" minSize={30}>
                        <ContentPanel entry={selectedEntry} />
                    </Panel>
                </Group>
            </Box>
        </InspectorProvider>
    );
}
